import { apiRequest } from '../config/api';

export interface NotificacaoRealtime {
  id: string;
  usuario_id: string;
  tipo: string;
  titulo: string;
  mensagem: string;
  data_criacao: string;
  lida: boolean;
  data_leitura?: string;
  dados?: any; // Campo adicional para dados extras
}

export class NotificacoesService {
  private static subscriptions: Map<string, any> = new Map();

  // Buscar notificações não lidas
  static async buscarNaoLidas(usuarioId: string): Promise<NotificacaoRealtime[]> {
    try {
      const result = await apiRequest('/notificacoes/nao-lidas');
      return result || [];
    } catch (error) {
      console.error('Erro ao buscar notificações não lidas:', error);
      return [];
    }
  }

  // Buscar notificações do usuário
  static async buscarDoUsuario(usuarioId: string, apenasNaoLidas?: boolean): Promise<NotificacaoRealtime[]> {
    try {
      const params = apenasNaoLidas ? '?apenas-nao-lidas=true' : '';
      const result = await apiRequest(`/notificacoes/usuario/${usuarioId}${params}`);
      return result.data || [];
    } catch (error) {
      console.error('Erro ao buscar notificações do usuário:', error);
      return [];
    }
  }

  // Contar notificações não lidas
  static async contarNaoLidas(usuarioId: string): Promise<number> {
    try {
      const result = await apiRequest('/notificacoes/nao-lidas/count');
      return result.count || 0;
    } catch (error) {
      console.error('Erro ao contar notificações não lidas:', error);
      return 0;
    }
  }

  // Inscrever para notificações em tempo real (desabilitado temporariamente)
  static inscrever(usuarioId: string, callback: (notificacao: NotificacaoRealtime) => void): string {
    try {
      // Retornar ID fake para não quebrar o sistema
      const subscriptionId = `notificacoes_${usuarioId}_${Date.now()}`;
      return subscriptionId;
    } catch (error) {
      console.error('Erro ao inscrever para notificações:', error);
      return `notificacoes_${usuarioId}_error`;
    }
  }

  // Desinscrever de notificações (desabilitado temporariamente)
  static desinscrever(subscriptionId: string): void {
    try {
      // Não fazer nada pois realtime está desabilitado
      this.subscriptions.delete(subscriptionId);
    } catch (error) {
      console.error('Erro ao desinscrever de notificações:', error);
    }
  }

  // Marcar notificação como lida
  static async marcarComoLida(notificacaoId: string): Promise<void> {
    try {
      await apiRequest(`/notificacoes/${notificacaoId}/lida`, {
        method: 'PATCH'
      });
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      throw error;
    }
  }

  // Marcar todas como lidas
  static async marcarTodasComoLidas(usuarioId: string): Promise<void> {
    try {
      await apiRequest('/notificacoes/marcar-todas-lidas', {
        method: 'PUT',
        body: JSON.stringify({ usuario_id: usuarioId })
      });
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
      throw error;
    }
  }

  // Criar notificação
  static async criar(notificacao: Omit<NotificacaoRealtime, 'id' | 'data_criacao' | 'lida' | 'data_leitura'>): Promise<NotificacaoRealtime> {
    try {
      const result = await apiRequest('/notificacoes', {
        method: 'POST',
        body: JSON.stringify(notificacao)
      });
      return result;
    } catch (error) {
      console.error('Erro ao criar notificação:', error);
      throw error;
    }
  }

  // Excluir notificação
  static async excluir(notificacaoId: string): Promise<void> {
    try {
      await apiRequest(`/notificacoes/${notificacaoId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
      throw error;
    }
  }

  // Métodos de notificação (simplificados - sem implementação real por enquanto)
  static async notificarCargaOrdemChegada(usuarioId: string, cargaId: string, solicitacaoId: string, ordem: number, horario: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarCargaPrioridadeMudada(usuarioId: string, cargaId: string, solicitacaoId: string, prioridadeAnterior: number, prioridadeNova: number): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarSolicitacaoAcessoCriada(solicitacaoId: string, nome: string, email: string, perfil: string, motivo: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarSolicitacaoAcessoAprovada(usuarioId: string, solicitacaoId: string, nome: string, email: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarSolicitacaoAcessoReprovada(usuarioId: string, solicitacaoId: string, nome: string, email: string, motivoReprovacao: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarEquipeNovaSolicitacao(solicitacaoId: string, fornecedor: string, tipoProduto: string, dataDesejada: string, filial: string, solicitanteId: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarCargaOcorrencia(usuarioId: string, cargaId: string, solicitacaoId: string, descricao: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarSolicitacaoAprovada(usuarioId: string, solicitacaoId: string, fornecedor: string, produto: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarSolicitacaoReprovada(usuarioId: string, solicitacaoId: string, fornecedor: string, motivo: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarSolicitacaoReagendada(usuarioId: string, solicitacaoId: string, fornecedor: string, novaData: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarPedidoCriado(usuarioId: string, solicitacaoId: string, numeroPedido: string): Promise<void> {
    // Método desabilitado temporariamente
  }

  static async notificarPedidoDigitado(usuarioId: string, solicitacaoId: string, numeroPedido: string): Promise<void> {
    // Método desabilitado temporariamente
  }
}

export default NotificacoesService;
